<script setup>
import { useBooksStore } from '~/stores/books'
import { useUserStore } from '~/stores/user'
import { useRouteStore } from '~/stores/route'
import { useReviewsStore } from '~/stores/reviews'
import { useOnboardingStore } from '~/stores/onboarding'
import { getMetaData, getStructuredData } from '~/utils/bookSeo'
import { PAGES } from '~/utils/constants'
import { useBook } from '~/composables/useBook'
import MonthlyBookBasicInfo from '~/components/monthlyBooks/MonthlyBookBasicInfo'
import MonthlyBookAlert from '~/components/monthlyBooks/MonthlyBookAlert'
import ChooseBookAlert from '~/components/basicSubscription/ChooseBookAlert'
import BookCharacteristics from '~/components/books/BookCharacteristics'
import BookDescription from '~/components/books/BookDescription'
import BecomeMemberBanner from '~/components/banners/BecomeMemberBanner'
import DetailPagePremiumBanner from '~/components/basicSubscription/DetailPagePremiumBanner'
import ReviewsSection from '~/components/reviews/ReviewsSection'
import RelatedBooksCarousel from '~/components/books/RelatedBooksCarousel'
import DoYouKnowBanner from '~/components/monthlyBooks/DoYouKnowBanner'

const {
  $sentryCaptureException,
  $capitalizeFirstLetter,
  $label,
  $gtmTracking
} = useNuxtApp()

const route = useRoute()
const { slug } = route.params
const userStore = useUserStore()
const loggedInUser = computed(() => userStore.loggedInUser)
const isAuthenticated = computed(() => userStore.isAuthenticated)
const canUpgradeToPremium = computed(() => userStore.canUpgradeToPremium)
const booksStore = useBooksStore()
const chosenBooksData = computed(() => booksStore.allChosenBooks)
const reviewsStore = useReviewsStore()

const {
  data: book,
  error,
  status,
  refresh
} = await useAsyncData(() => {
  reviewsStore.clearReviews()
  return booksStore.getBookDetails(slug)
})
const pending = computed(() => status.value === 'pending')

if (error.value) {
  const { statusCode, statusMessage } = error.value
  $sentryCaptureException(
    `status:${statusCode}, message: ${statusMessage || error.value}`
  )

  if (statusCode) {
    throw createError({
      fatal: true,
      statusCode,
      statusMessage: statusMessage || JSON.stringify(error.value)
    })
  }
}

if (!book.value) {
  throw createError({
    fatal: true,
    statusCode: 500,
    statusMessage: 'Empty book response'
  })
}

useHead({
  title: () =>
    book.value
      ? `${book.value.title} - ${book.value.author.name} • ebook • luisterboek • Bookchoice`
      : '',
  meta: getMetaData(book.value, route),
  script: [
    { type: 'application/ld+json', innerHTML: getStructuredData(book.value) }
  ]
})

const routeStore = useRouteStore()
const routeFrom = computed(() => routeStore.from)
const onboardingStore = useOnboardingStore()
const onboardingStep = computed(() => onboardingStore.step)
const onboardingVariant = computed(() => onboardingStore.variant)
const onboardingLabel = computed(() => onboardingStore.label)

watch(isAuthenticated, () => {
  if (navigator.onLine) {
    refresh()
  }
})
watch(chosenBooksData, (newData, oldData) => {
  const { categoryCode } = book.value
  if (
    newData[categoryCode]?.choicesLeft !== oldData[categoryCode]?.choicesLeft
  ) {
    refresh()
  }
})

const breadcrumb = computed(() => {
  if (!book.value.categoryCode) return null

  const prevPage = routeFrom?.value
  const linkSlug = transformMonthFromDate(book.value.categoryCode)
  const path = prevPage?.path?.includes(PAGES.archive)
    ? PAGES.archive
    : PAGES.monthlyBooks

  return {
    link: `${path}/${linkSlug}`,
    text: $label('books.breadcrumb', {
      breadcrumb: $capitalizeFirstLetter(book.value.categoryCode)
    })
  }
})
const ebookDownloadable = computed(() => {
  return !!(isAuthenticated.value && book.value.download?.ebook?.downloadable)
})
const audioBookDownloadable = computed(() => {
  return !!(
    isAuthenticated.value && book.value.download?.audioBook?.downloadable
  )
})
const bookDownloadable = computed(() => {
  return audioBookDownloadable.value || ebookDownloadable.value
})
const chosenBook = computed(() => book.value.bookChosenAsBasic)
const firstQuote = computed(() => book.value.quotes && book.value.quotes[0])
const bookCharacteristics = computed(() => book.value.characteristics)
const { isCurrentMonth } = useBook()
const canUpgrade = computed(() => {
  return (
    isCurrentMonth(book.value.categoryCode) &&
    canUpgradeToPremium.value &&
    !book.value.bookChosenAsBasic
  )
})
const secondQuote = computed(() => {
  return book.value.quotes && book.value.quotes[1]
})
const showReviewsWithBackground = computed(() => {
  return !secondQuote.value && loggedInUser.value && !canUpgrade.value
})
const showRelatedBooksWithBackground = computed(() => {
  return secondQuote.value || !loggedInUser.value || canUpgrade.value
})

const { genreToKey } = useBook()
const genre = genreToKey(book.value.genres)
const bgClassWithGenre = 'background-section--' + genre

function readBookClicked() {
  $gtmTracking.push({
    event: 'interact_event',
    eventAction: 'open',
    eventCategory: 'MonthlyBooksDetails || ebookReader',
    eventLabel: book.value.title
  })
}
</script>

<template>
  <div v-if="book">
    <MonthlyBookAlert
      :book="book"
      :book-downloadable="bookDownloadable"
      :chosen-book="chosenBook"
      :logged-in-user="loggedInUser"
    />

    <MonthlyBookBasicInfo
      :book="book"
      :breadcrumb="breadcrumb"
      :read-book-clicked="readBookClicked"
      :is-authenticated="isAuthenticated"
      :ebook-downloadable="ebookDownloadable"
      :audio-book-downloadable="audioBookDownloadable"
      :is-loading-details="pending"
      :onboarding-variant="onboardingVariant"
      :onboarding-step="onboardingStep"
      :onboarding-label="onboardingLabel"
    />

    <ChooseBookAlert v-if="book.isAvailableToChoose" :book="book" />

    <BaseQuote v-if="firstQuote" :quote="firstQuote" />

    <BookCharacteristics
      v-if="bookCharacteristics"
      :characteristics="bookCharacteristics"
      :genre="genre"
    />

    <BookDescription v-if="book.longEditorReview" :book="book" />

    <BecomeMemberBanner v-if="!loggedInUser" :genre="genre" />
    <DetailPagePremiumBanner v-else-if="canUpgrade" />
    <div v-else-if="secondQuote" :class="bgClassWithGenre">
      <BaseQuote :quote="secondQuote" second-quote-variant />
    </div>

    <div :class="[{ [bgClassWithGenre]: showReviewsWithBackground }]">
      <ReviewsSection :book="book" />
    </div>

    <div :class="[{ [bgClassWithGenre]: showRelatedBooksWithBackground }]">
      <RelatedBooksCarousel :slug="book.slug" />
    </div>

    <DoYouKnowBanner :logged-in-user="loggedInUser" />
  </div>
</template>

<style lang="scss" scoped>
@include generate-genre-classes(
  'background-color',
  'light',
  'background-section',
  0.5
);
</style>
